import { inspectionActive } from "../../../state/inspectionSchemas";
import {
  findingControlKey,
  setPendingFindingRun,
  usePendingFindingRun,
  stopFindingInspector,
  reviewEndpoint,
} from "../../../state/findingInspectorControl";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInspections } from "../../../state/inspections";
import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { RefreshCwIcon } from "lucide-react";
import { createFindingReviewSubmission } from "../../../state/findingReview";
import { useFindingComments, useSuggestionComments } from "../../../state/suggestionComments";
import { InspectorAvatar } from "../inspector/InspectionStatus";
import { FindingReviewProgress, FindingReviewReply } from "../inspector/FindingReviewActivity";
import { ReviewConversation, type ReviewTimelineEntry } from "../ReviewConversation";
import {
  belongsToChange,
  changeDiscussionPrefix,
  changeDiscussionKey,
  displayChangeComment,
  type ChangeDiscussion,
} from "./changes/changeDiscussion";

export function FindingConversation({
  organizationId,
  findingId,
  orgSegment,
  canAskAI,
  initialInspector = false,
  change,
  composerHost,
  recheck = false,
  composerLayout,
  history,
  subject = "finding",
}: {
  organizationId: string;
  findingId: string;
  orgSegment: string;
  canAskAI: boolean;
  initialInspector?: boolean;
  change?: ChangeDiscussion;
  composerHost?: HTMLElement | null;
  /**
   * Offer a one-click re-reading of this finding against the current document.
   * Set when the assessment was written against a revision the document has
   * since moved past, and Inspector is allowed to revise this finding.
   */
  recheck?: boolean;
  /** See `ReviewConversation`. */
  composerLayout?: "default" | "tabs";
  /**
   * The finding's events (raised, decided, planned, revised), merged into
   * this one timeline; the section then reads as the finding's activity.
   */
  history?: ReadonlyArray<ReviewTimelineEntry>;
  /**
   * A suggestion on a document is reviewed the same way, through its own
   * conversation and endpoint; `findingId` is then the suggestion's id.
   */
  subject?: "finding" | "suggestion";
}) {
  const suggestion = subject === "suggestion";
  const findingComments = useFindingComments(suggestion ? null : organizationId, findingId);
  const suggestionComments = useSuggestionComments(suggestion ? organizationId : null, findingId);
  const conversation = suggestion ? suggestionComments : findingComments;
  const reviews = useInspections(organizationId, findingId);
  const controlKey = findingControlKey(organizationId, findingId);
  const pendingRun = usePendingFindingRun(controlKey);
  const activeRun = reviews.snapshot?.runs.find(inspectionActive);
  const confirmedPending = reviews.snapshot?.runs.find((run) => run.threadId === pendingRun);
  const activeThreadId =
    activeRun?.threadId ?? (pendingRun !== "submitting" && !confirmedPending ? pendingRun : null);
  useEffect(() => {
    if (confirmedPending && !inspectionActive(confirmedPending))
      setPendingFindingRun(controlKey, null);
  }, [controlKey, confirmedPending]);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const inspectorStatus =
    activeThreadId || pendingRun === "submitting"
      ? `Inspector is already running for this ${subject}.`
      : reviews.loading
        ? "Checking Inspector…"
        : reviews.error
          ? "Refresh Inspector's status before starting another review."
          : null;
  const stop = async () => {
    if (!activeThreadId || stopping) return;
    setStopping(true);
    setStopError(null);
    const failure = await stopFindingInspector(organizationId, findingId, activeThreadId, subject);
    setStopError(failure);
    // Remain blocked until the run is confirmed stopped by the server.
    if (failure) setStopping(false);
    reviews.refresh();
  };
  useEffect(() => {
    if (!activeThreadId) setStopping(false);
  }, [activeThreadId]);
  const runs = (reviews.snapshot?.runs ?? []).filter(
    (run) => !change || belongsToChange(run.review?.instruction ?? "", change),
  );
  const prefix = change ? changeDiscussionPrefix(change) : "";
  const commentAnchorPrefix = change ? `${change.proposalId}-${change.index}-` : "";
  const [threadId, setThreadId] = useState<string | null>(null);
  const submit = useFindingReviewSubmission(
    organizationId,
    findingId,
    () => {
      conversation.refresh();
      reviews.refresh();
    },
    subject,
  );
  const askAI = async (text: string, files?: readonly File[]) => {
    if (inspectorStatus) return inspectorStatus;
    setPendingFindingRun(controlKey, "submitting");
    const result = await submit(
      prefix + (text || `Review the attached files for this ${subject}.`),
      files,
    );
    if ("error" in result) {
      setPendingFindingRun(controlKey, null);
      return result.error;
    }
    setPendingFindingRun(controlKey, result.threadId);
    setThreadId(result.threadId);
    return null;
  };
  const [rechecking, setRechecking] = useState(false);
  /**
   * Ask Inspector to re-read this finding against the document as it stands
   * now. The same submission path as any other request in this conversation —
   * one run at a time, stoppable, and recorded in the thread — with the
   * instruction written here instead of by the reviewer, because the reviewer
   * should not have to know the words that make a re-grounding happen.
   */
  const recheckNow = async () => {
    if (rechecking || inspectorStatus) return;
    setRechecking(true);
    setStopError(null);
    try {
      setStopError(
        await askAI(
          "The document has changed since this finding was assessed. Start with governance_get_policy_diff to see just what changed — if this finding's affected sections are not in it, the finding is untouched and still stands. Read the full text only if the diff cannot settle it. Check each affected section and required change against the current version: renumber the sections that moved, and narrow or drop the required changes the current text already makes. Revise the finding with governance_revise_finding even if nothing needed changing, passing affectedSections back unchanged — that is what records it as checked against this version; leaving it untouched keeps it reported as out of date. Say which required changes the current version already satisfies and which still stand. Do not resolve or close it.",
        ),
      );
    } finally {
      setRechecking(false);
    }
  };
  const [continuing, setContinuing] = useState<string | null>(null);
  const continueReview = async (run: (typeof runs)[number]) => {
    if (continuing || inspectorStatus) return;
    setContinuing(run.threadId);
    setStopError(null);
    try {
      const failure = await askAI(
        `Continue review ${run.threadId} from its saved progress. Check the existing proposed changes, then finish the original request without repeating completed work.`,
      );
      setStopError(failure);
    } finally {
      setContinuing(null);
    }
  };
  return (
    <section
      id={change ? undefined : "finding-conversation"}
      className="scroll-mt-6 space-y-5 border-t border-border/60 pt-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold">{change ? "Discuss this change" : history ? "Activity" : "Conversation"}</h2>
        {recheck && canAskAI && !change ? (
          <Button
            size="sm"
            variant="outline"
            disabled={rechecking || Boolean(inspectorStatus)}
            onClick={() => void recheckNow()}
          >
            <RefreshCwIcon aria-hidden className={cn(rechecking && "animate-spin")} />
            {rechecking ? "Asking Inspector…" : "Re-check against the current document"}
          </Button>
        ) : null}
      </div>
      <ReviewConversation
        allowAttachments
        {...(composerLayout === undefined ? {} : { composerLayout })}
        {...(composerHost !== undefined ? { composerHost } : {})}
        inspectorStatus={stopping ? "Stopping Inspector…" : inspectorStatus}
        onStop={activeThreadId && activeRun?.status !== "queued" ? () => void stop() : undefined}
        stopping={stopping}
        controlError={stopError}
        commentAnchorPrefix={commentAnchorPrefix}
        initialInspector={initialInspector || canAskAI}
        entityId={change ? `${findingId}:${changeDiscussionKey(change)}` : findingId}
        label={change ? "Change comments" : suggestion ? "Suggestion comments" : "Finding comments"}
        subject={subject}
        reservedCharacters={prefix.length}
        conversation={
          change
            ? {
                ...conversation,
                snapshot: conversation.snapshot
                  ? {
                      ...conversation.snapshot,
                      comments: conversation.snapshot.comments.filter((comment) =>
                        belongsToChange(comment.text, change),
                      ),
                    }
                  : null,
                postComment: (text) => conversation.postComment(prefix + text),
              }
            : conversation
        }
        {...(canAskAI ? { askAI } : {})}
        {...(change ? { formatCommentText: displayChangeComment } : {})}
        inspectorRequestIds={runs.flatMap((run) =>
          run.review?.requestId ? [run.review.requestId] : [],
        )}
        activity={[...(change ? [] : (history ?? [])), ...runs.flatMap((run): ReviewTimelineEntry[] => {
          const comment = conversation.snapshot?.comments.find(
            (item) => item.id === run.review?.requestId,
          );
          const entries: ReviewTimelineEntry[] = [
            {
              id: `run:${run.threadId}`,
              createdAt: run.startedAt,
              order: 1,
              indicator: <InspectorAvatar className="rounded-full" />,
              content: (
                <FindingReviewProgress
                  run={run}
                  orgSegment={orgSegment}
                  findingId={findingId}
                  showRequest={!comment}
                  {...(canAskAI && !inspectorStatus
                    ? { onContinue: () => void continueReview(run) }
                    : {})}
                  continuing={continuing !== null}
                />
              ),
            },
          ];
          if (run.status === "completed" && run.reply)
            entries.push({
              id: `reply:${run.threadId}`,
              createdAt: run.reply.createdAt,
              order: 2,
              indicator: <InspectorAvatar className="rounded-full" />,
              content: (
                <FindingReviewReply
                  run={run}
                  orgSegment={orgSegment}
                  findingId={findingId}
                  email={comment?.actorEmail ?? null}
                  requestVisible={Boolean(comment)}
                  commentAnchorPrefix={commentAnchorPrefix}
                />
              ),
            });
          return entries;
        })]}
      />
      {threadId && !runs.some((run) => run.threadId === threadId) ? (
        <p role="status" className="text-sm text-muted-foreground">
          Inspector received your request.{" "}
          <Link
            className="font-medium text-foreground underline underline-offset-4"
            to="/$org/compliance/inspector/$threadId"
            params={{ org: orgSegment, threadId }}
          >
            View run
          </Link>
        </p>
      ) : null}
      {reviews.error ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
        >
          {reviews.error}
          <Button size="sm" variant="outline" onClick={reviews.refresh}>
            <RefreshCwIcon aria-hidden />
            Try again
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** Keep request deduplication alive when comment pagination replaces its refresh callback. */
export function useFindingReviewSubmission(
  organizationId: string,
  findingId: string,
  refresh: () => void,
  subject: "finding" | "suggestion" = "finding",
) {
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);
  return useMemo(
    () =>
      createFindingReviewSubmission(reviewEndpoint(subject, organizationId, findingId), () =>
        refreshRef.current(),
      ),
    [organizationId, findingId, subject],
  );
}
